import { Button, LinearProgress } from '@tunasong/ui-lib'
import type { HTMLAttributes } from 'react'
import { graphHooks } from '@tunasong/graph-lib/react'
import type { Note } from '@tunasong/models'
import { matchAny, noteTemplate, shortUuid } from '@tunasong/models'
import { usePlugins } from '@tunasong/plugin-lib'
import { useCurrentUser, useMimeTypes } from '@tunasong/redux'
import { isMusicXML, isPdf } from '@tunasong/schemas'
import type { SidebarComponent } from '@tunasong/ui-lib'
import { Divider, EntityContent, Typography, UploadStoredEntity, VBox } from '@tunasong/ui-lib'
import { Fragment, type FC } from 'react'
import { useEditorClass } from '../hooks/index.js'

const contentFilter = matchAny(isPdf, isMusicXML)

const NoteEditorBox = (props: HTMLAttributes<HTMLDivElement>) => (
  <div
    style={{
      borderRight: '2px solid var(--tuna-palette-primary-main)',
      marginTop: 8,
      marginBottom: 8,
      padding: 8,
      flex: 1,
      display: 'flex',
    }}
  >
    {props.children}
  </div>
)

export const EntityNotes: FC<SidebarComponent> = ({ entity, style: styleProp }) => {
  const { entities: notes, isSuccess } = graphHooks.useEntityChildren<Note>({ parentId: entity?.id, filter: 'note' })
  const { userId } = useCurrentUser()

  const draftTemplate = noteTemplate({ id: shortUuid(), parentId: entity.id, userId })

  const { entities, isLoading } = graphHooks.useEntityChildren({
    parentId: entity?.id,
    filter: contentFilter,
  })
  const [deleteEntity, { isLoading: isDeleting }] = graphHooks.useEntityDelete()

  const NoteEditor = useEditorClass('NoteEditor')
  const { mimeTypeAccept } = useMimeTypes()

  const plugins = usePlugins('all') ?? []

  // In the case where we start using the draft, we need to filter out the draft from the notes
  const filteredNotes = notes.filter(n => n.id !== draftTemplate.id)
  const hasNote = isSuccess ? filteredNotes.length > 0 : undefined

  return (
    <VBox style={{ flex: 1, ...styleProp }}>
      <Divider>
        <Typography variant="caption">Personal Note</Typography>
      </Divider>
      {filteredNotes.map(note => (
        <NoteEditorBox key={note.id}>
          <NoteEditor element={note} gutter={false} />
        </NoteEditorBox>
      ))}
      {/* If there are no other notes, provide an empty note */}
      {!hasNote ? (
        <NoteEditorBox>
          <NoteEditor style={{ flex: 1 }} element={draftTemplate} gutter={false} />
        </NoteEditorBox>
      ) : null}

      <Divider>
        <Typography variant="caption">Documents</Typography>
      </Divider>
      {isLoading ? <LinearProgress /> : null}
      {entities.map(doc => (
        <Fragment key={doc.id}>
          <EntityContent key={doc.id} entity={doc} plugins={plugins} />
          <Button disabled={isDeleting} color="error" onClick={() => deleteEntity({ entity: doc })}>
            Delete
          </Button>
        </Fragment>
      ))}
      <UploadStoredEntity
        style={{ flex: 1 }}
        parentId={entity.id}
        contentHint="MusicXML or PDF"
        isPrivate={true}
        accept={mimeTypeAccept}
      />
    </VBox>
  )
}
